/**
 * The certificate of need's ventilation and heating page: any number of heating systems (stable
 * per-row ids), photovoltaics and the ventilation choices.
 */

import React from 'react'
import { Collapse, Stack, Typography } from '@mui/material'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { TransitionGroup } from 'react-transition-group'
import { useTranslation } from 'react-i18next'

import { GRAY_900 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'

import type { UseEnergyCertificateForm } from '../useEnergyCertificateForm'
import { MUIIconButton } from 'components/common/MUIIconButton'
import { HeaterRow } from './heating/HeaterRow'
import { PhotovoltaicSection } from './heating/PhotovoltaicSection'
import { VentilationSection } from './heating/VentilationSection'

export const VentilationAndHeatingPanel: React.FC<{ form: UseEnergyCertificateForm }> = ({ form }) => {
  const { t } = useTranslation(['heating_system'])
  const { view, heaterIds, addHeater, removeHeater } = form

  return (
    <Stack gap={2}>

      <TransitionGroup component={null}>
        {view.heaters.map((heater, index) => {
          const id = heaterIds[index]
          return (
            <Collapse key={id}>
              <Stack gap={1.6}>
                <SectionedBorderBox
                  title={
                    <Stack direction="row" justifyContent="space-between" alignItems="center">

                      <Typography variant="text-md" fontWeight={600} color={GRAY_900}>{`${t('heating_system_title')} #${index + 1}`}</Typography>

                      {index > 0 &&
                        <MUIIconButton variant="plain" size="lg" icon={<CloseRoundedIcon />} ariaLabel="delete-heating-system" onClick={() => removeHeater(id)} />
                      }

                    </Stack>
                  }
                >
                  <HeaterRow id={id} index={index} heater={heater} form={form} />
                </SectionedBorderBox>
              </Stack>
            </Collapse>
          )
        })}
      </TransitionGroup>

      <MUIDivider>
        <MUIButton type="secondaryBorder" startIcon={<AddCircleOutlineRoundedIcon fontSize="large" />} onClick={() => addHeater()}>
          {t('add_heating_system')}
        </MUIButton>
      </MUIDivider>

      <PhotovoltaicSection initial={{ system: view.photovoltaicSystem, features: view.photovoltaicFeatures }} form={form} />

      <VentilationSection initial={view.ventilationFeatures} form={form} />

    </Stack>
  )
}
